import React, { useState, useMemo } from 'react';
import axios from 'axios';
import {
  Sparkles, Type, Calendar, MapPin, Phone as PhoneIcon, Home, Loader2,
  CheckCircle2, User, Package, Palette,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import SectionHeading from './SectionHeading';
import CoconutCanvas from './CoconutCanvas';
import { CITIES } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = [
  { id: 'monogram', label: 'Monogram', hint: 'Elegant script initials' },
  { id: 'stamped',  label: 'Stamped',  hint: 'Crisp inked stamp' },
  { id: 'carved',   label: 'Carved',   hint: 'Deep hand-carved' },
  { id: 'photo',    label: 'AI Photo', hint: 'Photo-realistic engraving' },
];

const STYLE_META = {
  monogram: { font: "'Dancing Script', cursive", weight: 700, letterSpacing: '0.02em' },
  stamped:  { font: "'Playfair Display', serif", weight: 900, letterSpacing: '0.14em', textTransform: 'uppercase' },
  carved:   { font: "'Playfair Display', serif", weight: 700, letterSpacing: '0.04em', fontStyle: 'italic' },
  photo:    { font: "'Outfit', sans-serif",     weight: 700, letterSpacing: '0.02em' },
};

const emptyForm = {
  name: '',
  event_name: '',
  coconut_text: '',
  engraving_style: 'monogram',
  coconut_variant: 'brown',
  pieces: '',
  event_date: '',
  city: '',
  address: '',
  phone: '',
  email: '',
  notes: '',
};

const CoconutCustomizer = () => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // holds saved order or null

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const previewText = useMemo(() => {
    if (form.coconut_text.trim()) return form.coconut_text.trim();
    if (form.name.trim()) {
      // Only the very first letter of the name — single monogram initial.
      return form.name.trim().charAt(0).toUpperCase();
    }
    return 'MC';
  }, [form.name, form.coconut_text]);

  const styleMeta = STYLE_META[form.engraving_style] || STYLE_META.monogram;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const required = ['name', 'phone', 'event_name', 'coconut_text', 'pieces', 'city', 'address'];
    for (const k of required) {
      const val = form[k];
      if (val === '' || val == null) {
        setError('Please fill your Name, Phone, Event, Coconut text, Pieces, City and Address.');
        return;
      }
    }
    const pieces = Number(form.pieces);
    if (Number.isNaN(pieces) || pieces < 1) {
      setError('Pieces must be at least 1.');
      return;
    }
    if (pieces < 100) {
      setError('Minimum order is 100 coconuts per event.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        event_name: form.event_name.trim(),
        coconut_text: form.coconut_text.trim(),
        engraving_style: form.engraving_style,
        pieces,
        event_date: form.event_date || null,
        city: form.city,
        address: form.address.trim(),
        notes: form.notes.trim() || null,
      });
      setDone(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join(' · ')
          : 'Could not submit your order. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const startAnother = () => {
    setDone(null);
    setForm(emptyForm);
  };

  return (
    <section id="customize" className="section-pad bg-gradient-to-b from-blush via-cream to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <SectionHeading
          eyebrow="Make it yours"
          title="Customize Your"
          accent="Coconut"
          subtitle="Type a name, add an event, choose a style — see your personalised coconut come alive, then place your order."
        />

        <div className="mt-14 grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          {/* LEFT: Coconut live preview */}
          <div className="lg:col-span-2" data-testid="customizer-preview">
            <div className="sticky top-28">
              <div className="relative rounded-[2.5rem] overflow-hidden shadow-floating border border-pink-100 bg-gradient-to-br from-[#FFF7F0] via-[#FFE4EC]/50 to-[#FFF7F0] aspect-[4/5]">
                {/* Real coconut image with engraving overlay */}
                <CoconutCanvas>
                  <div
                    className="relative max-w-full text-center"
                    data-testid="customizer-preview-text"
                  >
                    <div
                      style={{
                        fontFamily: styleMeta.font,
                        fontWeight: styleMeta.weight,
                        letterSpacing: styleMeta.letterSpacing,
                        textTransform: styleMeta.textTransform,
                        fontStyle: styleMeta.fontStyle,
                        color: form.engraving_style === 'stamped' ? '#4A1D2E' : '#5C3D26',
                        textShadow: form.engraving_style === 'stamped'
                          ? '0 1px 1px rgba(0,0,0,0.25)'
                          : '0 1px 0 rgba(255,255,255,0.6), 0 -1px 0 rgba(60,30,15,0.35)',
                        WebkitTextStroke: form.engraving_style === 'carved' ? '0.5px rgba(74, 29, 46, 0.35)' : undefined,
                        opacity: form.engraving_style === 'carved' ? 0.85 : 1,
                      }}
                      className="text-3xl sm:text-4xl md:text-5xl leading-tight break-words"
                    >
                      {previewText}
                    </div>
                    {form.event_name && (
                      <div
                        className="mt-3 font-script text-[#5C3D26] text-base sm:text-lg tracking-wide"
                        style={{ textShadow: '0 1px 0 rgba(255,255,255,0.55)' }}
                      >
                        · {form.event_name} ·
                      </div>
                    )}
                  </div>
                </CoconutCanvas>

                {/* Floating tag chip */}
                <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur text-pink-brand text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                  <Sparkles className="w-3 h-3" /> Live Preview
                </div>

                {/* Bottom badge with pieces */}
                <div className="absolute bottom-4 right-4 rounded-2xl bg-white/95 backdrop-blur px-4 py-2 shadow-soft">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Order</div>
                  <div className="font-display font-bold text-stone-900 text-lg leading-none">
                    {form.pieces || '—'} <span className="text-xs font-medium text-stone-500">coconuts</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center text-xs text-stone-500">
                Preview only. Final engraving is A+ grade, hand-finished by our master craftsmen.
              </div>
            </div>
          </div>

          {/* RIGHT: Form or success */}
          <div className="lg:col-span-3">
            {done ? (
              <div
                className="rounded-3xl bg-white border border-pink-100 shadow-floating p-8 sm:p-10 text-center"
                data-testid="customizer-success"
              >
                <div className="mx-auto w-16 h-16 rounded-full bg-blush text-pink-brand flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <div className="mt-2 text-xs font-semibold uppercase tracking-widest text-pink-brand">Order placed</div>
                <h3 className="mt-2 font-display font-bold text-3xl text-stone-900">
                  Thank you, {done.name.split(' ')[0]}!
                </h3>
                <p className="mt-3 text-stone-600 max-w-lg mx-auto">
                  Your custom coconut order <span className="font-mono font-semibold text-stone-900">{done.order_no}</span> has been received.
                  Our team will confirm production on <span className="font-semibold text-stone-900">{done.phone}</span> within a day.
                </p>

                <dl className="mt-6 grid grid-cols-2 gap-3 text-left max-w-md mx-auto text-sm">
                  <div className="rounded-2xl bg-blush/60 border border-pink-100 p-3">
                    <dt className="text-[10px] uppercase tracking-widest text-pink-brand">Coconuts</dt>
                    <dd className="font-display font-bold text-stone-900 text-lg">{done.pieces}</dd>
                  </div>
                  <div className="rounded-2xl bg-blush/60 border border-pink-100 p-3">
                    <dt className="text-[10px] uppercase tracking-widest text-pink-brand">Engraving</dt>
                    <dd className="font-semibold text-stone-900">"{done.coconut_text}"</dd>
                  </div>
                  <div className="rounded-2xl bg-blush/60 border border-pink-100 p-3">
                    <dt className="text-[10px] uppercase tracking-widest text-pink-brand">Event</dt>
                    <dd className="font-semibold text-stone-900 truncate">{done.event_name}</dd>
                  </div>
                  <div className="rounded-2xl bg-blush/60 border border-pink-100 p-3">
                    <dt className="text-[10px] uppercase tracking-widest text-pink-brand">City</dt>
                    <dd className="font-semibold text-stone-900">{done.city}</dd>
                  </div>
                </dl>

                <Button
                  onClick={startAnother}
                  className="mt-8 rounded-full bg-pink-brand hover:bg-[#BE185D] text-white px-8 h-11 font-semibold shadow-pink-glow"
                  data-testid="customizer-place-another-button"
                >
                  Customize Another Order
                </Button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="rounded-3xl bg-white border border-pink-100 shadow-soft p-6 sm:p-8 grid gap-5"
                data-testid="customizer-form"
              >
                {/* Row 1 – identity */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
                      <User className="w-3 h-3 inline mr-1 text-pink-brand" /> Your Name *
                    </Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setField('name', e.target.value)}
                      placeholder="e.g. Priya Sharma"
                      className="mt-1"
                      data-testid="customizer-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
                      <PhoneIcon className="w-3 h-3 inline mr-1 text-pink-brand" /> Phone *
                    </Label>
                    <Input
                      value={form.phone}
                      onChange={(e) => setField('phone', e.target.value)}
                      placeholder="+91 98xxxxxxxx"
                      className="mt-1"
                      data-testid="customizer-phone-input"
                    />
                  </div>
                </div>

                {/* Row 2 – event */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
                      <Sparkles className="w-3 h-3 inline mr-1 text-pink-brand" /> Event Name *
                    </Label>
                    <Input
                      value={form.event_name}
                      onChange={(e) => setField('event_name', e.target.value)}
                      placeholder="e.g. Priya &amp; Rohan's Sangeet"
                      className="mt-1"
                      data-testid="customizer-event-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
                      <Calendar className="w-3 h-3 inline mr-1 text-pink-brand" /> Event Date
                    </Label>
                    <Input
                      type="date"
                      value={form.event_date}
                      onChange={(e) => setField('event_date', e.target.value)}
                      className="mt-1"
                      data-testid="customizer-date-input"
                    />
                  </div>
                </div>

                {/* Row 3 – engraving */}
                <div>
                  <Label className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
                    <Type className="w-3 h-3 inline mr-1 text-pink-brand" /> Print on Coconut *
                    <span className="ml-1 normal-case text-[10px] text-stone-400 tracking-normal">(name, initials, hashtag — max 40 chars)</span>
                  </Label>
                  <Input
                    value={form.coconut_text}
                    onChange={(e) => setField('coconut_text', e.target.value.slice(0, 40))}
                    placeholder='e.g. P ♡ R  or  #PriyaWedsRohan'
                    className="mt-1"
                    maxLength={40}
                    data-testid="customizer-coconut-text-input"
                  />
                  <div className="mt-1 text-[11px] text-stone-400 text-right">{form.coconut_text.length}/40</div>
                </div>

                {/* Style chooser */}
                <div>
                  <Label className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
                    <Palette className="w-3 h-3 inline mr-1 text-pink-brand" /> Engraving Style
                  </Label>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {STYLES.map((s) => {
                      const active = form.engraving_style === s.id;
                      return (
                        <button
                          type="button"
                          key={s.id}
                          onClick={() => setField('engraving_style', s.id)}
                          className={`text-left rounded-2xl border p-3 transition-all ${
                            active
                              ? 'bg-pink-brand text-white border-pink-brand shadow-pink-glow'
                              : 'bg-white text-stone-700 border-stone-200 hover:border-pink-brand hover:bg-blush/40'
                          }`}
                          data-testid={`customizer-style-${s.id}`}
                        >
                          <div className="text-sm font-bold font-display">{s.label}</div>
                          <div className={`text-[10px] mt-0.5 ${active ? 'text-white/85' : 'text-stone-500'}`}>{s.hint}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Row 4 – pieces / city */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
                      <Package className="w-3 h-3 inline mr-1 text-pink-brand" /> Number of Coconuts *
                      <span className="ml-1 normal-case text-[10px] text-stone-400 tracking-normal">(min 100)</span>
                    </Label>
                    <Input
                      type="number"
                      min={100}
                      value={form.pieces}
                      onChange={(e) => setField('pieces', e.target.value)}
                      placeholder="e.g. 250"
                      className="mt-1"
                      data-testid="customizer-pieces-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
                      <MapPin className="w-3 h-3 inline mr-1 text-pink-brand" /> City *
                    </Label>
                    <Select value={form.city} onValueChange={(v) => setField('city', v)}>
                      <SelectTrigger className="mt-1" data-testid="customizer-city-trigger">
                        <SelectValue placeholder="Choose a city" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {CITIES.map((c) => (
                          <SelectItem key={c} value={c} data-testid={`customizer-city-option-${c.toLowerCase()}`}>{c}</SelectItem>
                        ))}
                        <SelectItem value="Other" data-testid="customizer-city-option-other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Delivery address */}
                <div>
                  <Label className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
                    <Home className="w-3 h-3 inline mr-1 text-pink-brand" /> Delivery Address *
                  </Label>
                  <Textarea
                    rows={3}
                    value={form.address}
                    onChange={(e) => setField('address', e.target.value)}
                    placeholder="Venue name, area, landmark, pin code…"
                    className="mt-1"
                    data-testid="customizer-address-input"
                  />
                </div>

                {/* Optional email + notes */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
                      Email <span className="normal-case text-[10px] text-stone-400 tracking-normal">(optional)</span>
                    </Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setField('email', e.target.value)}
                      placeholder="you@example.com"
                      className="mt-1"
                      data-testid="customizer-email-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
                      Special Requests
                    </Label>
                    <Input
                      value={form.notes}
                      onChange={(e) => setField('notes', e.target.value)}
                      placeholder="Colour, ribbon, timing…"
                      className="mt-1"
                      data-testid="customizer-notes-input"
                    />
                  </div>
                </div>

                {error && (
                  <div
                    className="rounded-lg bg-red-50 text-red-700 text-sm px-4 py-2 border border-red-200"
                    data-testid="customizer-error"
                  >
                    {error}
                  </div>
                )}

                <div className="flex items-center justify-between gap-3 pt-2 flex-wrap">
                  <p className="text-[11px] text-stone-500 max-w-xs">
                    You'll get an order number instantly. We'll call you to confirm production &amp; payment.
                  </p>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="rounded-full bg-pink-brand hover:bg-[#BE185D] text-white h-12 px-8 font-semibold shadow-pink-glow disabled:opacity-70"
                    data-testid="customizer-submit-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Placing order…
                      </>
                    ) : (
                      <>Submit Order</>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoconutCustomizer;
